import React, { useMemo, useState } from "react";
import { Download, Loader2, Pencil } from "lucide-react";
import axiosInstance from "../api/axiosInstance";
import { formatAccountName, formatDateForDisplay, getManualFillMoneyMovement } from "../utils/formatters";
import StatusEditor from "./StatusEditor";
import Tooltip from "./Tooltip";
import HouseholdCommentsModal from "./HouseholdCommentsModal";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";
import { isSendToClientHiddenForProject } from "../utils/featureGates";
import { STATUS_LABELS } from "../constants/householdConstants";

const NEW_EDGE_FIRM_DOCS = [
  { key: "iaa", label: "IAA" },
  { key: "client_profile", label: "Client Profile" },
];

const WAVERLY_FIRM_DOCS = [
  { key: "planning_iaa", label: "Planning IAA" },
  { key: "without_planning_iaa", label: "Without Planning IAA" },
  { key: "business_planning_iaa", label: "Business Planning IAA" },
  { key: "business_without_planning_iaa", label: "Business Without Planning IAA" },
  { key: "trust_planning_iaa", label: "Irrevocable Trust Planning IAA" },
  { key: "trust_without_planning_iaa", label: "Irrevocable Trust Without Planning IAA" },
];

const FIRM_DOCS_CONFIG = {
  new_edge: {
    title: "New Edge Firm Documents",
    subtitle: "Applied to the whole household",
    docs: NEW_EDGE_FIRM_DOCS,
  },
  waverly: {
    title: "Waverly Firm Documents",
    subtitle: "Investment Advisory Agreements for this household",
    docs: WAVERLY_FIRM_DOCS,
  },
};

const SEND_FOR_REVIEW_DISABLED_STATUSES = [
  "docusign_initiated",
  "docusign_completed",
  "account_opened",
  "toa_completed",
  "nigo",
  "firm_paperwork_in_progress",
  "completed",
  "closed",
];

function HouseholdHeader({
  household,
  project,
  user,
  onDownloadForms,
  onSendToClient,
  onEnrichData,
  onReviewDocuments,
  onSendForSignature,
  sendForSignatureEnabled = false,
  downloadingForms,
  sendingToClient,
  sendingForSignature,
  hasPrimaryMemberWithEmail,
  onMarkPendingForExecution,
  onMarkDocusignInitiated,
  onMarkCompleted,
  onHouseholdStatusPatch,
  onHouseholdRefresh,
}) {
  const { toast } = useToast();
  const [manualFormsOpen, setManualFormsOpen] = useState(true);
  const [supplementalDocsOpen, setSupplementalDocsOpen] = useState(true);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [formsModalMode, setFormsModalMode] = useState("download");
  const [accountFormsData, setAccountFormsData] = useState({});
  const [selectedFormsForAccount, setSelectedFormsForAccount] = useState({});
  const [notSupportedAccountIds, setNotSupportedAccountIds] = useState([]);
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [firmDocs, setFirmDocs] = useState(null);
  const [firmDocsType, setFirmDocsType] = useState(null);
  const [selectedFirmDocs, setSelectedFirmDocs] = useState({});
  const [loadingAccountForms, setLoadingAccountForms] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [showSendToClientModal, setShowSendToClientModal] = useState(false);
  const [advisorNote, setAdvisorNote] = useState("");
  const [showHouseholdCommentsModal, setShowHouseholdCommentsModal] = useState(false);

  const specialCharPattern = /[@#$%*^]/;
  const hasSpecialChars = specialCharPattern.test(advisorNote);

  const completionPercentage = household.completion_percentage || 0;
  
  // Form display names
  const getFormDisplayName = (formType) => {
    const formDisplayNames = {
      'iws_ira': 'Retirement Application',
      'iws_simple_ira': 'Simple IRA Application',
      'iws_inherited_ira': 'Inherited IRA Application',
      'iws_business': 'Business Account Application',
      'iws_business_cert': 'Business Authorization Certification',
      'iws_application': 'Individual/Joint Application',
      'iws_tod': 'TOD',
      'iws_toa': 'TOA',
      'iws_trust_application': 'Trust Application',
      'iws_trust_cert': 'Trust Certificate',
      'iws_one_time_distribution': 'One Time Distribution',
      'iws_periodic_distribution': 'Periodic Distribution',
      'iws_spi': 'Standing Payment Instructions',
      'iws_margin': 'Margin Application',
      'iws_option': 'Options Application',
      'nfs_toa': 'TOA',
      'nfs_ira': 'Retirement Application',
      'nfs_brokerage': 'Trust Application',
      'nfs_trust_cert': 'Trust Certificate',
      'nfs_indi_joint_application': 'Individual/Joint Application',
      'nfs_retirement_spi': 'SPI - Retirement',
      'nfs_non_retirement_spi': 'SPI - Non Retirement',
      'nfs_periodic_distribution': 'Periodic Distribution',
      'nfs_margin': 'Margin Application',
      'nfs_option': 'Options Application'
    };
    return formDisplayNames[formType] || formType;
  };

  // Ordered form arrays for display
  const iwsFormOrder = [
    'iws_application',
    'iws_ira',
    'iws_simple_ira',
    'iws_inherited_ira',
    'iws_business',
    'iws_business_cert',
    'iws_trust_application',
    'iws_tod',
    'iws_trust_cert',
    'iws_toa',
    'iws_spi',
    'iws_periodic_distribution',
    'iws_one_time_distribution',
    'iws_margin',
    'iws_option'
  ];

  const nfsFormOrder = [
    'nfs_indi_joint_application',
    'nfs_ira',
    'nfs_brokerage',
    'nfs_trust_cert',
    'nfs_toa',
    'nfs_non_retirement_spi',
    'nfs_retirement_spi',
    'nfs_periodic_distribution',
    'nfs_margin',
    'nfs_option'
  ];

  const manualFillGroups = useMemo(() => {
    const withdrawal = [];
    const contribution = [];
    const unsupported = [];
    (household?.accounts || []).forEach((account, idx) => {
      const label = formatAccountName(account, idx + 1, household?.members || []);
      const { withdrawalUnsupported, contributionUnsupported } =
        getManualFillMoneyMovement(account);
      if (notSupportedAccountIds.includes(account.id)) {
        unsupported.push(label);
        return;
      }
      if (withdrawalUnsupported) withdrawal.push(label);
      if (contributionUnsupported) contribution.push(label);
    });
    return { withdrawal, contribution, unsupported };
  }, [household?.accounts, household?.members, notSupportedAccountIds]);

  const hasManualFillItems =
    manualFillGroups.withdrawal.length > 0 ||
    manualFillGroups.contribution.length > 0 ||
    manualFillGroups.unsupported.length > 0;

  // Fetch all account forms
  const fetchAllAccountForms = async () => {
    if (!household?.id || !household?.accounts || household.accounts.length === 0) {
      return;
    }

    setLoadingAccountForms(true);
    try {
      const response = await axiosInstance.get(
        `/forms/project/${project.id}/households/${household.id}/forms`
      );
      
      const formsData = response.data;

      // Capture accounts that require manual form fill
      setNotSupportedAccountIds(
        Array.isArray(formsData?.not_supported_accounts)
          ? formsData.not_supported_accounts
          : []
      );
      setSupportingDocs(
        Array.isArray(formsData?.supporting_documents)
          ? formsData.supporting_documents
          : []
      );

      // Process forms for each account
      household.accounts.forEach(account => {
        const accountId = account.id;
        const accountForms = formsData[accountId];
        
        if (!accountForms) {
          return; // Skip if no forms data for this account
        }

        // Determine if account is IWS or NFS
        const isIWS = accountForms.iws !== null && accountForms.iws !== undefined;
        const isNFS = accountForms.nfs !== null && accountForms.nfs !== undefined;
        
        if (!isIWS && !isNFS) {
          return; // Skip if no forms available
        }

        const formsForAccount = isIWS ? accountForms.iws : accountForms.nfs;
        
        // Store forms data
        setAccountFormsData(prev => ({
          ...prev,
          [accountId]: {
            type: isIWS ? 'iws' : 'nfs',
            forms: formsForAccount
          }
        }));

        // Pre-select forms that are true
        const preSelected = {};
        Object.keys(formsForAccount).forEach(formType => {
          if (formsForAccount[formType] === true) {
            preSelected[formType] = true;
          }
        });
        
        setSelectedFormsForAccount(prev => ({
          ...prev,
          [accountId]: preSelected
        }));
      });

      let docsSource = null;
      let docsType = null;
      if (project?.destination_firm === "NewEdge" && formsData?.firm_docs?.new_edge) {
        docsSource = formsData.firm_docs.new_edge;
        docsType = "new_edge";
      } else if (project?.destination_firm === "Waverly" && formsData?.firm_docs?.waverly) {
        const waverlyDocs = formsData.firm_docs.waverly;
        if (WAVERLY_FIRM_DOCS.some(({ key }) => waverlyDocs[key] === true)) {
          docsSource = waverlyDocs;
          docsType = "waverly";
        }
      }
      if (docsSource) {
        setFirmDocs(docsSource);
        setFirmDocsType(docsType);
        const preSelectedDocs = {};
        FIRM_DOCS_CONFIG[docsType].docs.forEach(({ key }) => {
          preSelectedDocs[key] = docsSource[key] === true;
        });
        setSelectedFirmDocs(preSelectedDocs);
      } else {
        setFirmDocs(null);
        setFirmDocsType(null);
        setSelectedFirmDocs({});
      }
    } catch (error) {
      console.error("Error fetching account forms:", error);
    } finally {
      setLoadingAccountForms(false);
    }
  };

  const handleFormToggle = (accountId, formType) => {
    setSelectedFormsForAccount(prev => ({
      ...prev,
      [accountId]: {
        ...(prev[accountId] || {}),
        [formType]: !(prev[accountId]?.[formType] || false)
      }
    }));
  };

  const handleFirmDocToggle = (key) => {
    setSelectedFirmDocs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRestoreSelections = () => {
    const restored = {};
    Object.keys(accountFormsData).forEach(accountId => {
      const accountForms = accountFormsData[accountId]?.forms || {};
      const preSelected = {};
      Object.keys(accountForms).forEach(formType => {
        if (accountForms[formType] === true) {
          preSelected[formType] = true;
        }
      });
      restored[accountId] = preSelected;
    });
    setSelectedFormsForAccount(restored);

    if (firmDocs && firmDocsType) {
      const restoredDocs = {};
      FIRM_DOCS_CONFIG[firmDocsType].docs.forEach(({ key }) => {
        restoredDocs[key] = firmDocs[key] === true;
      });
      setSelectedFirmDocs(restoredDocs);
    }
  };

  const handleClearSelections = () => {
    const cleared = {};
    Object.keys(accountFormsData).forEach(accountId => {
      cleared[accountId] = {};
    });
    setSelectedFormsForAccount(cleared);
    if (firmDocs) setSelectedFirmDocs({});
  };

  const totalSelectedForms = Object.values(selectedFormsForAccount).reduce(
    (acc, formsObj) => acc + Object.values(formsObj || {}).filter(Boolean).length,
    0
  );
  const totalSelectedFirmDocs = firmDocs
    ? Object.values(selectedFirmDocs).filter(Boolean).length
    : 0;
  const noFormsSelected = totalSelectedForms + totalSelectedFirmDocs === 0;

  const firmDocsConfig = firmDocsType ? FIRM_DOCS_CONFIG[firmDocsType] : null;
  const offeredFirmDocs = firmDocsConfig
    ? firmDocsType === "waverly"
      ? firmDocsConfig.docs.filter(({ key }) => firmDocs?.[key] === true)
      : firmDocsConfig.docs
    : [];
  const showFirmDocsSection =
    !!firmDocs &&
    offeredFirmDocs.length > 0 &&
    (formsModalMode === "download" || firmDocsType === "waverly");
  const firmDocsCountForSignature =
    firmDocsType === "waverly" ? totalSelectedFirmDocs : 0;
  const totalSelectedForSignature = totalSelectedForms + firmDocsCountForSignature;

  const buildSelectedFirmDocsPayload = () => {
    if (!firmDocs || !firmDocsConfig) return undefined;
    return {
      [firmDocsType]: firmDocsConfig.docs.reduce((acc, { key }) => {
        acc[key] = !!selectedFirmDocs[key];
        return acc;
      }, {}),
    };
  };

  // Handle opening modal
  const openFormsModal = async (mode) => {
    setFormsModalMode(mode);
    setShowDownloadModal(true);
    await fetchAllAccountForms();
  };

  const handleOpenDownloadModal = () => openFormsModal("download");

  const handleOpenDocusignModal = () => openFormsModal("docusign");

  // Handle closing modal
  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    setAccountFormsData({});
    setSelectedFormsForAccount({});
    setNotSupportedAccountIds([]);
    setSupportingDocs([]);
    setFirmDocs(null);
    setFirmDocsType(null);
    setSelectedFirmDocs({});
  };

  const buildSelectedAccountForms = () => {
    const account_forms = {};
    Object.keys(selectedFormsForAccount).forEach(accountId => {
      const selections = selectedFormsForAccount[accountId] || {};
      const selectedKeys = Object.keys(selections).filter(k => selections[k]);
      if (selectedKeys.length > 0) {
        account_forms[accountId] = {};
        selectedKeys.forEach(k => {
          account_forms[accountId][k] = true;
        });
      }
    });
    return account_forms;
  };

  // Handle bulk download all forms
  const handleBulkDownloadAllForms = async () => {
    if (!household?.id || !project?.id) {
      toast.error("Missing required information.");
      return;
    }

    const account_forms = buildSelectedAccountForms();

    const firm_docs = buildSelectedFirmDocsPayload();

    const selectedFirmDocCount = firm_docs
      ? Object.values(firm_docs[firmDocsType]).filter(Boolean).length
      : 0;

    if (Object.keys(account_forms).length === 0 && selectedFirmDocCount === 0) {
      toast.error("No forms selected to download.");
      return;
    }

    setIsDownloadingAll(true);
    try {
      const response = await axiosInstance.post(
        `/households/project/${project.id}/households/${household.id}/download-all`,
        { account_forms, ...(firm_docs ? { firm_docs } : {}) },
        {
          responseType: 'blob',
          headers: {
            'Accept': 'application/zip'
          }
        }
      );
      
      let filename = 'All_Forms.zip';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        let filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
        }
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].trim();
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Forms downloaded.");
      handleCloseDownloadModal();
    } catch (error) {
      console.error("Error downloading forms:", error);
      toast.error(humanizeError(error));
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleCreateEnvelopeFromSelection = async () => {
    const account_forms = buildSelectedAccountForms();
    const firm_docs =
      firmDocsType === "waverly" ? buildSelectedFirmDocsPayload() : undefined;
    const selectedFirmDocCount = firm_docs
      ? Object.values(firm_docs.waverly).filter(Boolean).length
      : 0;
    if (Object.keys(account_forms).length === 0 && selectedFirmDocCount === 0) {
      toast.error("Select at least one form to send for signature.");
      return;
    }
    const succeeded = await onSendForSignature(account_forms, firm_docs);
    if (succeeded) {
      handleCloseDownloadModal();
    }
  };

  const isHighCompletion = completionPercentage > 80;




  const getSendButtonText = () => {
    if (['shared_with_client', 'shared_with_client_onboarding_done', 'shared_with_client_for_final_review'].includes(household.status)) {
      return 'Remind Client';
    }
    return 'Request Client Info';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Top Row - Household Name and Action Buttons */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2V7z" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">{household.name}</h1>
            <p className="text-base text-gray-600 mt-1">{project.title}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Enrich Data button */}
          <button 
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap transition-colors"
            onClick={onEnrichData}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Enrich Record
          </button>

          {/* Request Client Info / Send for Review button */}
          {!isSendToClientHiddenForProject(project?.title || project?.name) && (() => {
            const isStatusBlocked = SEND_FOR_REVIEW_DISABLED_STATUSES.includes(household.status);
            const blockedLabel = isStatusBlocked
              ? STATUS_LABELS[household.status] || household.status
              : null;
            return (
          <div
            className="relative inline-block"
            title={
              isStatusBlocked
                ? `Cannot send when in ${blockedLabel} status`
                : !hasPrimaryMemberWithEmail
                ? "No email exists for the client"
                : isHighCompletion ? "Send For Review" : "Request Client Info"
            }
          >
            <button
              onClick={() => { setAdvisorNote(""); setShowSendToClientModal(true); }}
              disabled={sendingToClient || !hasPrimaryMemberWithEmail || isStatusBlocked}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
            >
              {sendingToClient ? (
                <>
                  <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {isHighCompletion ? 'Send For Review' : getSendButtonText()}
                </>
              )}
            </button>
          </div>
            );
          })()}

          {/* Download Forms button */}
          <button
            onClick={handleOpenDownloadModal}
            disabled={downloadingForms}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Populate Forms
          </button>

          {/* Placeholder buttons for Edit History and Document Folder
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap transition-colors"
            title="Edit History"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Edits Log
          </button> */}
          
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap transition-colors"
            title="Document Folder"
            onClick={onReviewDocuments}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Document Folder
          </button>

          {onSendForSignature && (
          <button
            onClick={handleOpenDocusignModal}
            disabled={!sendForSignatureEnabled || sendingForSignature}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Create Docusign Envelope"
          >
            {sendingForSignature ? (
              <>
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Create Docusign Envelope
              </>
            )}
          </button>
          )}
        </div>
      </div>

      {/* Info Row - Creation Date, Advisor, Completion, Status, Comments */}
      <div className="grid grid-cols-5 gap-6 mb-6">
        <div className="flex min-h-0 flex-col">
          <label className="block text-base font-medium text-gray-500 mb-1">Creation Date</label>
          <div className="flex min-h-[2.75rem] flex-1 items-center">
            <p className="text-base font-semibold text-gray-900">{formatDateForDisplay(household.created_at)}</p>
          </div>
        </div>
        
        <div className="flex min-h-0 flex-col">
          <label className="block text-base font-medium text-gray-500 mb-1">Advisor</label>
          <div className="flex min-h-[2.75rem] flex-1 items-center">
            <p className="text-base font-semibold text-gray-900">
              {(() => {
                const advisorId = household?.advisor_id;
                const advisors = project?.advisors;
                if (advisorId === undefined || advisorId === null || !advisors) {
                  return "Unassigned";
                }

                let advisor = null;
                // Case 1: advisor_id is an index/key into project.advisors
                if (advisors[advisorId]) {
                  advisor = advisors[advisorId];
                } else {
                  // Case 2: advisor_id is a user_id within advisors entries
                  const advisorList = Array.isArray(advisors)
                    ? advisors
                    : Object.values(advisors);
                  advisor = advisorList.find(
                    (a) => String(a?.user_id || "") === String(advisorId),
                  );
                }

                if (!advisor) return "Unassigned";
                return `${advisor.first_name || ""} ${advisor.last_name || ""}`.trim() || "Unassigned";
              })()}
            </p>
          </div>
        </div>
        
        <div className="flex min-h-0 flex-col">
          <label className="block text-base font-medium text-gray-500 mb-1">Completion</label>
          <div className="flex min-h-[2.75rem] flex-1 items-center">
            <div className="flex w-full items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.floor(completionPercentage)}%` }}
                ></div>
              </div>
              <span className="text-base font-semibold text-gray-900">{Math.floor(completionPercentage)}%</span>
            </div>
          </div>
        </div>
        
        <div className="flex min-h-0 min-w-0 flex-col">
          <label className="block text-base font-medium text-gray-500 mb-1">Status</label>
          <div className="flex min-h-[2.75rem] flex-1 items-center">
            <StatusEditor
              household={household}
              userRole={user?.role}
              onStatusUpdated={onHouseholdStatusPatch}
              onNigoApplied={onHouseholdRefresh}
            />
          </div>
        </div>

        <div className="flex min-h-0 min-w-0 flex-col">
          <label className="block text-base font-medium text-gray-500 mb-1">Comments</label>
          <div className="flex min-h-[2.75rem] flex-1 items-center">
            <button
              type="button"
              onClick={() => setShowHouseholdCommentsModal(true)}
              className="relative inline-flex items-center gap-2 rounded-lg border border-emerald-600/40 bg-emerald-50/90 px-3 py-1.5 text-emerald-900 shadow-sm transition-colors hover:border-emerald-600/60 hover:bg-emerald-100"
            >
              <svg className="h-3.5 w-3.5 shrink-0 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-xs font-bold tracking-tight">Household Comments</span>
              {(household.unread_comment_count || 0) > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold leading-none ring-2 ring-white">
                  {household.unread_comment_count > 99 ? "99+" : household.unread_comment_count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Download Forms Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseDownloadModal}>
          <div className="bg-white rounded-xl shadow-xl max-w-xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {formsModalMode === "docusign"
                    ? "Forms to be sent for signature"
                    : "Forms that will be populated"}
                </h2>
                <button
                  onClick={handleCloseDownloadModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {loadingAccountForms ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="ml-2 text-sm text-gray-600">Loading forms...</span>
                </div>
              ) : household?.accounts && household.accounts.length > 0 ? (
                <div className="space-y-4">
                  {household?.investor_associations?.shareholder_or_member_board_director && (
                    <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded-lg p-3">
                      <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-sm text-amber-900">
                        One or more members of this household are a 10% shareholder or a board member of a public company.
                      </p>
                    </div>
                  )}
                  {hasManualFillItems && (
                    <div className="border border-amber-200 bg-amber-50 rounded-lg">
                      <button
                        type="button"
                        onClick={() => setManualFormsOpen(!manualFormsOpen)}
                        className="w-full flex items-center gap-2 p-3 text-left"
                      >
                        <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <h3 className="text-sm font-semibold text-amber-900 flex-1">Forms to be Manually Filled</h3>
                        <svg className={`w-4 h-4 text-amber-600 flex-shrink-0 transition-transform duration-200 ${manualFormsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {manualFormsOpen && (
                        <div className="space-y-2 pl-9 pr-3 pb-3">
                          {[
                            { key: "withdrawal", title: "Withdrawal not supported", labels: manualFillGroups.withdrawal },
                            { key: "contribution", title: "Contribution not supported", labels: manualFillGroups.contribution },
                            { key: "unsupported", title: "Unsupported accounts", labels: manualFillGroups.unsupported },
                          ]
                            .filter((group) => group.labels.length > 0)
                            .map((group) => (
                              <div key={group.key} className="flex flex-wrap items-center gap-1.5 min-w-0">
                                <span className="text-xs font-semibold text-amber-900">
                                  {group.title}:
                                </span>
                                {group.labels.map((label) => (
                                  <Tooltip key={label} content={label}>
                                    <span className="inline-block max-w-[14rem] truncate rounded-full border border-amber-300 bg-amber-100/70 px-2 py-0.5 text-xs text-amber-900">
                                      {label}
                                    </span>
                                  </Tooltip>
                                ))}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                  {supportingDocs.length > 0 && (
                    <div className="border border-sky-200 bg-sky-50 rounded-lg">
                      <button
                        type="button"
                        onClick={() => setSupplementalDocsOpen(!supplementalDocsOpen)}
                        className="w-full flex items-center gap-2 p-3 text-left"
                      >
                        <svg className="w-4 h-4 text-sky-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-sm font-semibold text-sky-900 flex-1">Supplemental documents</h3>
                        <svg className={`w-4 h-4 text-sky-600 flex-shrink-0 transition-transform duration-200 ${supplementalDocsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {supplementalDocsOpen && (
                        <div className="space-y-2 pl-9 pr-3 pb-3">
                          {supportingDocs.some((d) => d.status === "included") && (
                            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                              <span className="text-xs font-semibold text-sky-900">
                                {formsModalMode === "docusign" ? "Included in envelope:" : "Included in download:"}
                              </span>
                              {supportingDocs
                                .filter((d) => d.status === "included")
                                .map((doc) => (
                                  <span
                                    key={doc.bank_account_id}
                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800"
                                  >
                                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {`${doc.bank_name || "Bank"} ****${doc.account_number_last4 || "N/A"} — ${doc.document_type === "Check" ? "Voided Check" : doc.document_type}`}
                                  </span>
                                ))}
                            </div>
                          )}
                          {supportingDocs.some((d) => d.status === "missing") && (
                            <p className="text-xs text-sky-900">
                              Voided check or bank statement needed for:{" "}
                              {supportingDocs
                                .filter((d) => d.status === "missing")
                                .map((doc) => `${doc.bank_name || "Bank"} ****${doc.account_number_last4 || "N/A"}`)
                                .join(", ")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {(Object.keys(accountFormsData).length > 0 || firmDocs) && (
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={handleRestoreSelections}
                        title="Reset to the original selection returned by the system"
                        className="text-xs px-2.5 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors font-semibold inline-flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M4 10a8 8 0 0114-4M20 14a8 8 0 01-14 4" />
                        </svg>
                        Reset
                      </button>
                      <button
                        type="button"
                        onClick={handleClearSelections}
                        title="Deselect all forms"
                        className="text-xs px-2.5 py-1 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors font-semibold inline-flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear
                      </button>
                    </div>
                  )}
                  {showFirmDocsSection && (
                    <div className="border border-gray-200 rounded-lg p-3">
                      <div className="mb-3">
                        <h3 className="text-base font-semibold text-gray-900 leading-snug">
                          {firmDocsConfig.title}
                        </h3>
                        <div className="text-xs text-gray-600 mt-1">
                          {firmDocsConfig.subtitle}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        {offeredFirmDocs.map(({ key, label }) => {
                          const isChecked = !!selectedFirmDocs[key];
                          return (
                            <label
                              key={key}
                              className="flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-gray-50"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleFirmDocToggle(key)}
                                className={`w-3.5 h-3.5 border-gray-300 rounded focus:ring-emerald-500 ${isChecked ? 'text-emerald-600 bg-emerald-600 border-emerald-600' : 'text-gray-400'}`}
                              />
                              <span className={`text-xs ${isChecked ? 'text-emerald-700 font-medium' : 'text-gray-700'}`}>{label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {household.accounts.map((account, accIdx) => {
                    const accountFormsInfo = accountFormsData[account.id];
                    if (!accountFormsInfo) {
                      return null;
                    }

                    const formOrder = accountFormsInfo.type === 'iws' ? iwsFormOrder : nfsFormOrder;
                    const availableForms = accountFormsInfo.forms;

                    return (
                      <div key={account.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="mb-3 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-base font-semibold text-gray-900 leading-snug break-words">
                              {formatAccountName(
                                account,
                                accIdx + 1,
                                household.members || [],
                              )}
                            </h3>
                            <div className="text-xs text-gray-600 mt-1">
                              {accountFormsInfo.type === 'iws' ? 'IWS Forms' : 'NFS Forms'}
                            </div>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-emerald-200/90 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-800 text-[11px] font-semibold tracking-wide whitespace-nowrap shadow-sm">
                            {account.type || "N/A"}
                          </span>
                        </div>

                        <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                          {formOrder
                            .filter((formType) =>
                              Object.prototype.hasOwnProperty.call(availableForms, formType),
                            )
                            .map((formType) => {
                            const isEditable = availableForms[formType] === true;
                            const isChecked = isEditable && !!selectedFormsForAccount[account.id]?.[formType];

                            return (
                              <label
                                key={formType}
                                className={`flex items-center gap-2 p-1.5 rounded ${isEditable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-60'}`}
                                title={!isEditable ? 'Not available for this account' : undefined}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={!isEditable}
                                  onChange={() => isEditable && handleFormToggle(account.id, formType)}
                                  className={`w-3.5 h-3.5 border-gray-300 rounded focus:ring-emerald-500 disabled:cursor-not-allowed ${isChecked ? 'text-emerald-600 bg-emerald-600 border-emerald-600' : 'text-gray-400'}`}
                                />
                                <span className={`text-xs ${isChecked ? 'text-emerald-700 font-medium' : isEditable ? 'text-gray-700' : 'text-gray-400'}`}>{getFormDisplayName(formType)}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  <div className="flex gap-2 pt-3 border-t border-gray-200">
                    {formsModalMode === "docusign" ? (
                      <button
                        onClick={handleCreateEnvelopeFromSelection}
                        disabled={sendingForSignature || totalSelectedForSignature === 0}
                        title={totalSelectedForSignature === 0 ? "Select at least one form to send" : undefined}
                        className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5 flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingForSignature ? (
                          <>
                            <Loader2 className="animate-spin h-3.5 w-3.5" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Pencil className="w-3.5 h-3.5" />
                            {`Create Docusign Envelope${totalSelectedForSignature > 0 ? ` (${totalSelectedForSignature})` : ""}`}
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={handleBulkDownloadAllForms}
                        disabled={downloadingForms || isDownloadingAll || noFormsSelected}
                        title={noFormsSelected ? "Select at least one form to populate" : undefined}
                        className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5 flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingForms || isDownloadingAll ? (
                          <>
                            <Loader2 className="animate-spin h-3.5 w-3.5" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-3.5 h-3.5" />
                            Populate Forms
                          </>
                        )}
                      </button>
                    )}

                    <button
                      onClick={handleCloseDownloadModal}
                      className="btn-ghost text-xs px-3 py-1.5"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-600">No accounts found or no forms available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <HouseholdCommentsModal
        isOpen={showHouseholdCommentsModal}
        onClose={() => setShowHouseholdCommentsModal(false)}
        householdId={household?.id}
        projectId={project?.id}
        currentUserId={user?.id}
        userRole={user?.role}
        onMarkPendingForExecution={onMarkPendingForExecution}
        onMarkDocusignInitiated={onMarkDocusignInitiated}
        onMarkCompleted={onMarkCompleted}
        status={household?.status}
        subStatus={household?.sub_status}
      />

      {/* Send to Client Modal */}
      {showSendToClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowSendToClientModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">
                  {isHighCompletion ? 'Send For Review' : getSendButtonText()}
                </h2>
                <button
                  onClick={() => setShowSendToClientModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add a note for the client <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={advisorNote}
                  onChange={(e) => {
                    if (e.target.value.length <= 200) {
                      setAdvisorNote(e.target.value);
                    }
                  }}
                  placeholder="e.g. Please review and complete the missing fields."
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:border-emerald-500 resize-none ${
                    hasSpecialChars
                      ? 'border-red-400 focus:ring-red-300'
                      : 'border-gray-300 focus:ring-emerald-500'
                  }`}
                  rows={3}
                  maxLength={200}
                />
                <div className="flex justify-between mt-1">
                  {hasSpecialChars ? (
                    <span className="text-xs text-red-500">
                      Special characters (@, #, $, %, *, ^) are not allowed
                    </span>
                  ) : (
                    <span />
                  )}
                  <span className={`text-xs ${advisorNote.length >= 200 ? 'text-red-500' : 'text-gray-400'}`}>
                    {advisorNote.length}/200
                  </span>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowSendToClientModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowSendToClientModal(false);
                    onSendToClient(advisorNote.trim() || null);
                  }}
                  disabled={sendingToClient || hasSpecialChars}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingToClient ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HouseholdHeader;